import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { rolesApi, permissionsApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, PencilSimple, Trash, CircleNotch, Shield } from '@phosphor-icons/react';
import { formatDate, formatApiErrorDetail } from '../lib/utils';
import { toast } from 'sonner';

export default function RolesPage() {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState({ name: '', description: '', permissions: [] });

  const loadData = useCallback(async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([rolesApi.list(), permissionsApi.list()]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({ name: role.name || '', description: role.description || '', permissions: role.permissions || [] });
    } else {
      setEditingRole(null);
      setFormData({ name: '', description: '', permissions: [] });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingRole) { await rolesApi.update(editingRole.id, formData); toast.success('Role updated'); }
      else { await rolesApi.create(formData); toast.success('Role created'); }
      setDialogOpen(false);
      loadData();
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try { await rolesApi.delete(id); toast.success('Role deleted'); loadData(); }
    catch { toast.error('Failed to delete'); }
  };

  const togglePermission = (permCode) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permCode)
        ? prev.permissions.filter(p => p !== permCode)
        : [...prev.permissions, permCode],
    }));
  };

  const toggleModule = (modulePerms) => {
    const codes = modulePerms.map(p => p.code);
    const allSelected = codes.every(c => formData.permissions.includes(c));
    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !codes.includes(p))
        : [...new Set([...prev.permissions, ...codes])],
    }));
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    const module = perm.module || 'Other';
    if (!acc[module]) acc[module] = [];
    acc[module].push(perm);
    return acc;
  }, {});

  const permissionLabel = (code) => {
    const match = permissions.find((p) => p.code === code);
    return match?.name || code;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="roles-page">
        <PageHeader
          title={t('roles.title')}
          description={t('roles.manageRoles')}
          actions={(
            <Button onClick={() => handleOpenDialog()} data-testid="add-role-btn">
              <Plus className="mr-2 h-4 w-4" /> {t('roles.addRole')}
            </Button>
          )}
        />

        <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className={`${isDark ? 'border-zinc-800' : 'border-zinc-200'} hover:bg-transparent`}>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{t('roles.roleName')}</TableHead>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{t('common.description')}</TableHead>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{t('roles.permissionsLabel')}</TableHead>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{t('common.created')}</TableHead>
                  <TableHead className={`${isDark ? 'text-zinc-400' : 'text-zinc-500'} text-right`}>{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8"><CircleNotch className="w-6 h-6 animate-spin mx-auto text-brand-500" /></TableCell></TableRow>
                ) : roles.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className={`text-center py-8 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('roles.noRoles')}</TableCell></TableRow>
                ) : roles.map(role => (
                  <TableRow key={role.id} className={`${isDark ? 'border-zinc-800 hover:bg-zinc-800/50' : 'border-zinc-200 hover:bg-zinc-50'}`} data-testid={`role-row-${role.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-md bg-brand-500/10 flex items-center justify-center">
                          <Shield weight="fill" className="w-5 h-5 text-brand-500" />
                        </div>
                        <span className={`font-medium ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{role.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{role.description || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[300px]">
                        {(role.permissions || []).slice(0, 3).map(perm => (
                          <span key={perm} className={`px-2 py-0.5 rounded text-xs border ${isDark ? 'bg-zinc-800 text-zinc-300 border-zinc-700' : 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}>{permissionLabel(perm)}</span>
                        ))}
                        {(role.permissions || []).length > 3 && (
                          <span className={`px-2 py-0.5 rounded text-xs border ${isDark ? 'bg-zinc-800 text-zinc-500 border-zinc-700' : 'bg-zinc-100 text-zinc-500 border-zinc-200'}`}>+{role.permissions.length - 3} more</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{formatDate(role.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleOpenDialog(role)}
                          className={isDark ? 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}
                          data-testid={`edit-role-${role.id}`}><PencilSimple className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(role.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          data-testid={`delete-role-${role.id}`}><Trash className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white border-zinc-200 text-zinc-900'} max-w-xl max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle className="font-heading">{editingRole ? t('roles.editRole') : t('roles.addRole')}</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {editingRole ? t('roles.editDesc') : t('roles.addDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('common.name')} *</Label>
                <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="role-name-input" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('common.description')}</Label>
                <Input value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="role-description-input" />
              </div>
              <div className="space-y-3">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('roles.permissionsLabel')}</Label>
                {Object.entries(groupedPermissions).map(([module, perms]) => {
                  const allChecked = perms.every(p => formData.permissions.includes(p.code));
                  return (
                    <div key={module} className={`p-3 rounded-md border ${isDark ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-50 border-zinc-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-xs tracking-[0.1em] uppercase font-semibold ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{module}</p>
                        <label className={`flex items-center gap-1.5 text-[10px] cursor-pointer ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                          <Checkbox checked={allChecked} onCheckedChange={() => toggleModule(perms)} className={`h-3 w-3 ${isDark ? 'border-zinc-600' : 'border-zinc-300'}`} />
                          All
                        </label>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {perms.map(perm => (
                          <label key={perm.code} className={`flex items-center gap-2 text-sm cursor-pointer ${isDark ? 'text-zinc-300 hover:text-zinc-100' : 'text-zinc-600 hover:text-zinc-900'}`}>
                            <Checkbox checked={formData.permissions.includes(perm.code)} onCheckedChange={() => togglePermission(perm.code)}
                              className={isDark ? 'border-zinc-600 data-[state=checked]:bg-brand-600' : 'border-zinc-300 data-[state=checked]:bg-brand-600'} />
                            {perm.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}
                className={isDark ? 'border-zinc-700 text-zinc-300' : ''} data-testid="cancel-role-btn">{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving || !formData.name}
                className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="save-role-btn">
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
